import { motion } from "framer-motion";
import { Instagram, Music2, Youtube, Radio } from "lucide-react";

import { ARTIST, LABELS, RADIO_RESIDENCY } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { scrollToId } from "@/components/site/SmoothScroll";

// Small monochrome SVG icons for services without lucide equivalents
const SoundcloudIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c0-.057-.045-.1-.09-.1zm-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c0 .055.045.094.09.094s.089-.045.104-.104l.21-1.319-.21-1.334c0-.061-.044-.09-.09-.09zm1.83-1.229c-.061 0-.12.045-.12.104l-.21 2.585.225 2.475c0 .06.045.105.105.105s.105-.045.12-.105l.24-2.475-.24-2.586c0-.06-.061-.104-.121-.104zm.9-.089c-.061 0-.12.045-.12.104l-.181 2.686.181 2.505c0 .056.045.106.105.106.06 0 .1-.045.121-.104l.21-2.505-.21-2.688c0-.058-.045-.104-.105-.104zm.914-.045c-.061 0-.135.056-.135.135l-.15 2.686.15 2.505c0 .07.06.135.135.135.075 0 .135-.06.15-.135l.181-2.505-.18-2.686c0-.075-.075-.135-.15-.135zm.945-.735c-.075 0-.15.06-.15.15l-.15 3.436.15 2.505c0 .09.075.15.15.15s.166-.06.166-.15l.164-2.505-.164-3.436c0-.09-.075-.15-.166-.15zm.93.104c-.09 0-.164.075-.164.164l-.121 3.316.121 2.508c0 .09.074.164.164.164s.166-.074.166-.164l.15-2.508-.15-3.315c0-.09-.075-.166-.166-.166zm.93-.181c-.089 0-.164.075-.164.166l-.121 3.494.121 2.505c0 .09.075.166.164.166.091 0 .167-.076.167-.166l.135-2.505-.135-3.494c0-.09-.076-.166-.167-.166zm.945.024c-.089 0-.164.076-.164.166v.104l-.121 3.386.121 2.505c0 .09.075.166.164.166.091 0 .166-.076.166-.166l.135-2.505-.135-3.49c0-.09-.075-.166-.166-.166zm2.85-.615c-.06 0-.106.03-.145.075-.03.045-.045.104-.045.165l-.075 4.184.075 2.505c0 .105.09.196.195.196.15 0 .21-.09.21-.196l.09-2.505-.09-4.184c0-.121-.09-.24-.24-.24z"/>
  </svg>
);

const SpotifyIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0Zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02Zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2Zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.02.599-1.559.3Z"/>
  </svg>
);

const BeatportIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm-1.176 4.5h1.99v6.1l3.62-2.09.995 1.725-4.615 2.665v.15l4.616 2.664-.996 1.725-3.62-2.09v6.1h-1.99v-6.1L7.204 17.44l-.995-1.725 4.615-2.665v-.15L6.209 10.235l.996-1.725 3.619 2.09V4.5Z"/>
  </svg>
);

const socialLinks = [
  { id: "spotify", label: "Spotify", href: ARTIST.spotifyArtistUrl, Icon: SpotifyIcon },
  { id: "instagram", label: "Instagram", href: ARTIST.instagramUrl, Icon: (p) => <Instagram size={p.size ?? 14} /> },
  { id: "youtube", label: "YouTube", href: ARTIST.youtubeChannelUrl, Icon: (p) => <Youtube size={p.size ?? 14} /> },
  { id: "beatport", label: "Beatport", href: ARTIST.beatportArtist, Icon: BeatportIcon },
  { id: "soundcloud", label: "SoundCloud", href: ARTIST.soundcloudUrl, Icon: SoundcloudIcon },
];

const navLinks = [
  { label: "Releases", id: "releases" },
  { label: "Journey", id: "journey" },
  { label: "About", id: "about" },
  { label: "Book", id: "book" },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative w-full bg-bg-2 border-t border-[color:var(--line)]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(201,169,97,0.10), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10 py-16 md:py-20">
        {/* Signature */}
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-b border-[color:var(--line)] pb-14"
        >
          <motion.div variants={fadeUp}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-gold">
              Global bookings
            </p>
            <h3 className="mt-3 font-display text-4xl md:text-6xl font-black uppercase tracking-tighter text-ink">
              Irwan <span className="font-editorial text-gold-hi">Craw</span>
            </h3>
            <p className="mt-3 font-editorial text-lg text-[color:var(--gold-hi)]/80 max-w-md">
              Afro · Deep · Melodic · Latin House
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-col items-start md:items-end gap-2">
            <a
              data-testid="footer-email"
              href={`mailto:${ARTIST.email}`}
              className="font-editorial text-2xl md:text-3xl text-ink hover:text-[color:var(--gold-hi)] transition-colors"
            >
              {ARTIST.email}
            </a>
            <button
              data-testid="footer-book-cta"
              onClick={() => scrollToId("book")}
              className="mt-3 inline-flex items-center gap-3 rounded-full border border-[color:var(--gold)]/50 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.28em] text-[color:var(--gold-hi)] transition-all duration-500 hover:bg-[color:var(--gold)] hover:text-[#0a0a0a]"
            >
              Book Irwan Craw
              <span className="h-1 w-1 rounded-full bg-current" />
            </button>
          </motion.div>
        </motion.div>

        {/* 4-column grid */}
        <motion.div
          variants={stagger(0.08, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-14"
        >
          {/* Official Releases */}
          <motion.div variants={fadeUp}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold mb-5">
              Official Releases
            </p>
            <ul className="space-y-2.5">
              {LABELS.map((l) => (
                <li key={l.name} className="text-sm text-ink/90 font-medium">
                  {l.name}
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-muted mt-0.5">
                    {l.sub}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resident Artist */}
          <motion.div variants={fadeUp}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold mb-5">
              Resident Artist
            </p>
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--gold)] text-[#0a0a0a] shrink-0">
                <Radio size={12} />
              </span>
              <div>
                <p className="text-sm font-semibold text-ink">
                  {RADIO_RESIDENCY.name}
                </p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted mt-0.5">
                  Broadcasting worldwide
                </p>
              </div>
            </div>
          </motion.div>

          {/* Navigate */}
          <motion.div variants={fadeUp}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold mb-5">
              Navigate
            </p>
            <ul className="space-y-2.5">
              {navLinks.map((n) => (
                <li key={n.id}>
                  <button
                    data-testid={`footer-nav-${n.id}`}
                    onClick={() => scrollToId(n.id)}
                    className="group inline-flex items-center gap-2 text-sm text-ink/85 hover:text-[color:var(--gold-hi)] transition-colors"
                  >
                    <span className="h-px w-3 bg-[color:var(--line-strong)] transition-all duration-500 group-hover:w-6 group-hover:bg-[color:var(--gold)]" />
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Follow */}
          <motion.div variants={fadeUp}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold mb-5">
              Follow
            </p>
            <ul className="grid grid-cols-1 gap-2">
              {socialLinks.map((s) => (
                <li key={s.id}>
                  <a
                    data-testid={`footer-social-${s.id}`}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group inline-flex items-center gap-3 text-sm text-ink/85 hover:text-[color:var(--gold-hi)] transition-colors"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[color:var(--line-strong)] text-gold transition-all duration-500 group-hover:border-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-[#0a0a0a]">
                      <s.Icon size={12} />
                    </span>
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>

        {/* Bottom bar */}
        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-[color:var(--line)] pt-8 text-[10px] uppercase tracking-[0.28em] text-muted">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} Irwan Craw · All rights reserved
          </span>
          <div className="flex items-center gap-6">
            <button
              data-testid="footer-privacy"
              onClick={() => window.alert("Privacy policy coming soon.")}
              className="hover:text-[color:var(--gold-hi)] transition-colors"
            >
              Privacy Policy
            </button>
            <span className="font-editorial text-sm normal-case tracking-normal text-[color:var(--gold-hi)]/70">
              Ibiza · Tulum · Mykonos · Burning Man
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
